import React, { memo, useCallback } from 'react';

import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { InputLabel } from '@picsio/ui';

import localization from '../../../../shared/strings';
import {
  attachAggregatedKeyword,
  detachAggregatedKeyword,
} from '../../../../store/products/actions';
import { AGGREGATED_FIELD_STATE } from '../../../../store/products/helpers/aggregatedFieldState';
import { selectFirstSelectedProductId } from '../../../../store/selectors/aggregatedProductDetailsSelectors';
import Keywords from '../../Keywords';
import { aggregatedKeywordsFieldPropType } from '../helpers/aggregatedFieldPropTypes';
import { mapAggregatedKeywordsToList } from '../helpers/mapAggregatedKeywordsToList';
import {
  showAggregatedAttachKeywordDialog,
  showAggregatedDetachKeywordDialog,
} from '../helpers/aggregatedFieldDialogs';
import AggregatedScalarField from './AggregatedScalarField';

const l10n = localization.PRODUCT_INFO;
const aggregatedL10n = l10n.aggregated;

const AggregatedKeywords = ({
  field,
  readOnly,
  readOnlyTooltip,
}) => {
  const dispatch = useDispatch();
  const productId = useSelector(selectFirstSelectedProductId);
  const fieldLabel = l10n.autoMappedFieldLabels.keywords;
  const { keywords, isMultiple } = mapAggregatedKeywordsToList(field);

  const onAddKeyword = useCallback((keywordName) => {
    showAggregatedAttachKeywordDialog({
      keywordName,
      field,
      onConfirm: () => dispatch(attachAggregatedKeyword({ keywordName })),
    });
  }, [dispatch, field]);

  const onRemoveKeyword = useCallback((keywordIds) => {
    showAggregatedDetachKeywordDialog({
      field,
      onConfirm: () => dispatch(detachAggregatedKeyword({ keywordIds })),
    });
  }, [dispatch, field]);

  if (!field || field.state === AGGREGATED_FIELD_STATE.NOT_APPLICABLE) return null;

  if (isMultiple && readOnly) {
    return (
      <AggregatedScalarField
        label={fieldLabel}
        value=""
        placeholder={aggregatedL10n.multipleValues}
        readOnly={readOnly}
        showClearButton={false}
      />
    );
  }

  if (!productId) return null;

  const keywordsField = (
    <Keywords
      productId={productId}
      keywords={keywords}
      readOnly={readOnly}
      readOnlyTooltip={readOnly ? readOnlyTooltip : null}
      filterText={isMultiple ? aggregatedL10n.multipleValues : null}
      onAddKeyword={onAddKeyword}
      onRemoveKeyword={onRemoveKeyword}
    />
  );

  if (readOnly) {
    return keywordsField;
  }

  return (
    <>
      <InputLabel position="top" margin>
        {fieldLabel}
      </InputLabel>
      {keywordsField}
    </>
  );
};

AggregatedKeywords.propTypes = {
  field: aggregatedKeywordsFieldPropType,
  readOnly: PropTypes.bool,
  readOnlyTooltip: PropTypes.string,
};

AggregatedKeywords.defaultProps = {
  field: null,
  readOnly: false,
  readOnlyTooltip: null,
};

export default memo(AggregatedKeywords);
